/**
 * GroupRidesPage · v2.7.65+E238
 * =============================
 * Information-only public page (/group-rides). NO booking form or data
 * collection. Fully CMS-managed via /site/group-rides (the `page` block):
 * customizable background, header, descriptive text, photos, a Book Now
 * button (configurable link) and the dispatch phone + email below the text.
 *
 * Rendered as a theme-immune dark band (the marketing site is theme-aware;
 * see App.css `.ps-heading` rule) so it stays readable in light & dark.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import api from "@/lib/api";
import { Loader2, Phone, Mail, ArrowRight, Users } from "lucide-react";

export default function GroupRidesPage() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site/group-rides")
      .then((r) => setPage(r.data?.page || {}))
      .catch(() => setPage({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <MarketingLayout>
        <div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500" /></div>
      </MarketingLayout>
    );
  }

  const p = page || {};
  const useImage = p.bg_mode === "image" && p.bg_image_url;
  const bgStyle = useImage
    ? { backgroundImage: `url(${p.bg_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: p.bg_color || "#0b0b0c" };
  const phone = (p.dispatch_phone || "").trim();
  const email = (p.dispatch_email || "").trim();
  const link = p.book_now_link || "/ride/login";
  const isExternal = /^https?:\/\//i.test(link);
  const label = p.book_now_label || "Book Now";
  const photos = (p.photos || []).filter((ph) => ph.image_url);

  const BookNow = () => {
    const cls = "inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black px-8 py-3 rounded-lg text-sm uppercase tracking-wide transition-colors";
    return isExternal
      ? <a href={link} target="_blank" rel="noopener noreferrer" className={cls} data-testid="group-rides-book-now">{label}<ArrowRight className="h-4 w-4" /></a>
      : <Link to={link} className={cls} data-testid="group-rides-book-now">{label}<ArrowRight className="h-4 w-4" /></Link>;
  };

  return (
    <MarketingLayout>
      <section className="relative py-16 sm:py-20" style={bgStyle} data-testid="group-rides-page">
        {useImage && <div className="absolute inset-0" style={{ backgroundColor: "rgba(9,9,11,0.8)" }} aria-hidden="true" />}
        <div className="max-w-6xl mx-auto px-5 relative">
          <div className="text-center mb-6">
            {p.eyebrow && <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: "#fbbf24" }}>{p.eyebrow}</div>}
            <h1 className="ps-heading text-4xl sm:text-5xl font-black mt-2" style={{ color: "#f5f5f5" }} data-testid="group-rides-header">
              {p.header || "Group Rides"}
            </h1>
          </div>

          {p.intro && (
            <p className="text-base leading-relaxed max-w-3xl mx-auto text-center" style={{ color: "#d4d4d8" }} data-testid="group-rides-intro">
              {p.intro}
            </p>
          )}

          {photos.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="group-rides-photos">
              {photos.map((ph, i) => (
                <div key={ph.id || i} className="rounded-xl overflow-hidden border" style={{ borderColor: "#27272a" }} data-testid={`group-rides-photo-${i}`}>
                  <img src={ph.image_url} alt={ph.caption || "Group ride"} loading="lazy" decoding="async" className="w-full h-56 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  {ph.caption && <div className="p-3 text-sm" style={{ backgroundColor: "rgba(24,24,27,0.94)", color: "#a1a1aa" }}>{ph.caption}</div>}
                </div>
              ))}
            </div>
          )}

          <div className="mt-12 flex flex-col items-center gap-5">
            <BookNow />
            <div className="flex flex-col items-center gap-2 text-sm" style={{ color: "#d4d4d8" }}>
              {phone && (
                <a href={`tel:${phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-2 hover:text-amber-300" data-testid="group-rides-phone">
                  <Phone className="h-4 w-4" style={{ color: "#fbbf24" }} />{phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="inline-flex items-center gap-2 hover:text-amber-300" data-testid="group-rides-email">
                  <Mail className="h-4 w-4" style={{ color: "#fbbf24" }} />{email}
                </a>
              )}
            </div>
            {photos.length === 0 && <Users className="h-10 w-10 mt-2" style={{ color: "rgba(251,191,36,0.5)" }} />}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
